import type { PathStep } from '~/pages/backoffice/admin/sessions/path-step'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { AlertTriangle, GripVertical, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { cn } from '~/lib/utils'

/**
 * Une étape du parcours en cours d'édition : poignée de glisser-déposer, rang,
 * titre, date d'ouverture, retrait, et l'avertissement de date éventuel.
 */
function PathStepRow({
  step,
  title,
  index,
  warning,
  onRemove,
  onDateChange,
  children,
}: {
  step: PathStep
  title: string
  index: number
  warning?: string
  onRemove: () => void
  onDateChange: (value: string) => void
  /** Réglages sous la ligne, sur toute sa largeur : la condition d'ouverture (#80). */
  children?: React.ReactNode
}) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.moduleId,
  })

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-md border border-border bg-background p-2',
        isDragging && 'z-10 opacity-80 shadow-lg',
      )}
    >
      <button
        type="button"
        className="cursor-grab touch-none rounded p-1 text-muted-foreground hover:bg-muted active:cursor-grabbing"
        aria-label={t('sessions.path.reorder', { title })}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" />
      </button>

      <span className="w-6 shrink-0 text-center text-sm tabular-nums text-muted-foreground">
        {index + 1}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-medium">{title}</span>

      <div className="flex items-center gap-1.5">
        <label className="text-xs text-muted-foreground" htmlFor={`unlock-${step.moduleId}`}>
          {t('sessions.path.opensOn')}
        </label>
        <Input
          id={`unlock-${step.moduleId}`}
          type="date"
          className="h-8 w-[9.5rem]"
          value={step.unlockAt}
          onChange={e => onDateChange(e.target.value)}
        />
      </div>

      <Button
        type="button"
        variant="ghost"
        className="size-8 shrink-0 p-0"
        onClick={onRemove}
        aria-label={t('sessions.path.remove', { title })}
      >
        <X className="size-4" />
      </Button>

      {children}

      {warning && (
        <p className="flex w-full items-center gap-1.5 text-xs text-warning-text">
          <AlertTriangle className="size-3.5 shrink-0" />
          {warning}
        </p>
      )}
    </div>
  )
}

export default PathStepRow
